import { create } from 'zustand';

import {
  CDefaultTheme,
  EThemeMode,
  STORE_THEME_KEY,
  type TResolvedTheme,
} from '@common/types';

type TThemeState = {
  mode: EThemeMode;
  resolved: TResolvedTheme;
  setMode: (mode: EThemeMode) => void;
  setResolved: (resolved: TResolvedTheme) => void;
};

const readInitialMode = (): EThemeMode => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return CDefaultTheme;
  }
  const stored = window.localStorage.getItem(
    STORE_THEME_KEY,
  ) as EThemeMode | null;
  return stored && Object.values(EThemeMode).includes(stored)
    ? stored
    : CDefaultTheme;
};

export const getSystemTheme = (): TResolvedTheme => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return EThemeMode.LIGHT;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? EThemeMode.DARK
    : EThemeMode.LIGHT;
};

export const resolveTheme = (mode: EThemeMode): TResolvedTheme =>
  mode === EThemeMode.SYSTEM ? getSystemTheme() : (mode as TResolvedTheme);

const initialMode = readInitialMode();

const themeStore = create<TThemeState>((set) => ({
  mode: initialMode,
  resolved: resolveTheme(initialMode),
  setMode: (mode) => set({ mode, resolved: resolveTheme(mode) }),
  setResolved: (resolved) => set({ resolved }),
}));

export const useTheme = () => {
  const mode = themeStore((state) => state.mode);
  const resolved = themeStore((state) => state.resolved);
  const setMode = themeStore((state) => state.setMode);
  const setResolved = themeStore((state) => state.setResolved);
  return { mode, resolved, setMode, setResolved };
};
